import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';
import MarkSaleModal from './MarkSaleModal';
import AgentEntryModal from './AgentEntryModal';

const fmtMoney = (n) => 'Rs ' + (Math.round((n || 0) * 100) / 100).toLocaleString('en-PK');
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-PK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');

const ProfitShareSection = () => {
  const [agents, setAgents] = useState([]);
  const [agentId, setAgentId] = useState(null);
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  const [showMark, setShowMark] = useState(false);
  const [entryMode, setEntryMode] = useState(null); // 'expense' | 'advance' | null

  // month filter — defaults to current month (YYYY-MM)
  const ym = (dt) => `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}`;
  const currentMonth = ym(new Date());
  const [month, setMonth] = useState(currentMonth);

  // load agents once
  useEffect(() => {
    (async () => {
      try {
        const res = await chemicalService.getAgents();
        const list = res.data || [];
        setAgents(list);
        if (list.length > 0) setAgentId(list[0].id);
      } catch (err) {
        console.error('Error loading agents:', err);
      }
    })();
  }, []);

  const loadLedger = async (id, mo = month) => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await chemicalService.getAgentLedger(id, mo);
      setLedger(res.data || null);
    } catch (err) {
      console.error('Error loading ledger:', err);
      alert('❌ Failed to load ledger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (agentId) loadLedger(agentId, month); }, [agentId, month]);

  const refresh = () => {
    setShowMark(false);
    setEntryMode(null);
    loadLedger(agentId, month);
  };

  const handleUnmark = async (markId, invoiceNo) => {
    if (!window.confirm(`Remove invoice ${invoiceNo} from this agent's orders?`)) return;
    try {
      await chemicalService.unmarkSale(markId);
      loadLedger(agentId, month);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || err.message));
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!window.confirm('Delete this entry?')) return;
    try {
      await chemicalService.deleteAgentEntry(entryId);
      loadLedger(agentId, month);
    } catch (err) {
      alert('❌ ' + (err.response?.data?.message || err.message));
    }
  };

  // build last 13 months (current + 12 back) for the picker
  const monthOptions = [];
  {
    const d = new Date();
    for (let i = 0; i < 13; i++) {
      const dd = new Date(d.getFullYear(), d.getMonth() - i, 1);
      const val = ym(dd);
      const label = dd.toLocaleDateString('en-PK', { month: 'long', year: 'numeric' });
      monthOptions.push({ val, label });
    }
  }

  const agent = agents.find((a) => a.id === agentId) || null;
  const t = ledger?.totals || { total_share: 0, total_expense: 0, total_advance: 0, balance: 0 };

  return (
    <div style={styles.wrap}>
      {/* Header: agent selector + actions */}
      <div style={styles.topBar}>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <div style={styles.agentPick}>
            <label style={styles.pickLabel}>Agent</label>
            <select
              value={agentId || ''}
              onChange={(e) => setAgentId(Number(e.target.value))}
              style={styles.select}
            >
              {agents.map((a) => (
                <option key={a.id} value={a.id}>{a.name} ({a.profit_share_pct}%)</option>
              ))}
            </select>
          </div>
          <div style={styles.agentPick}>
            <label style={styles.pickLabel}>Month</label>
            <select value={month} onChange={(e) => setMonth(e.target.value)} style={styles.select}>
              {monthOptions.map((m) => (
                <option key={m.val} value={m.val}>{m.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div style={styles.actions}>
          <button style={styles.btnPrimary} onClick={() => setShowMark(true)} disabled={!agent}>+ Mark a Sale</button>
          <button style={styles.btnGreen} onClick={() => setEntryMode('expense')} disabled={!agent}>+ Expense</button>
          <button style={styles.btnRed} onClick={() => setEntryMode('advance')} disabled={!agent}>− Advance</button>
        </div>
      </div>

      {/* Totals summary */}
      <div style={styles.totalsRow}>
        <div style={styles.totalCard}>
          <div style={styles.totalLabel}>Profit Share (40%)</div>
          <div style={{ ...styles.totalVal, color: '#8e44ad' }}>{fmtMoney(t.total_share)}</div>
        </div>
        <div style={styles.totalCard}>
          <div style={styles.totalLabel}>+ Reimbursable Expenses</div>
          <div style={{ ...styles.totalVal, color: '#27ae60' }}>{fmtMoney(t.total_expense)}</div>
        </div>
        <div style={styles.totalCard}>
          <div style={styles.totalLabel}>− Advances Drawn</div>
          <div style={{ ...styles.totalVal, color: '#e74c3c' }}>{fmtMoney(t.total_advance)}</div>
        </div>
        <div style={{ ...styles.totalCard, ...styles.balanceCard }}>
          <div style={{ ...styles.totalLabel, color: 'rgba(255,255,255,0.85)' }}>Salary Payable (This Month)</div>
          <div style={{ ...styles.totalVal, color: 'white' }}>{fmtMoney(t.balance)}</div>
        </div>
      </div>

      {loading ? (
        <div style={styles.empty}>Loading ledger...</div>
      ) : !ledger ? (
        <div style={styles.empty}>No data.</div>
      ) : (
        <>
          {/* Marked orders with full breakdown */}
          <div style={styles.sectionLabel}>Marked Orders — Net Profit Breakdown</div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Invoice</th>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.thR}>Revenue</th>
                  <th style={styles.thR}>COGS</th>
                  <th style={styles.thR}>Expenses</th>
                  <th style={styles.thR}>Net Profit</th>
                  <th style={styles.thR}>Share ({agent?.profit_share_pct}%)</th>
                  <th style={styles.thC}></th>
                </tr>
              </thead>
              <tbody>
                {ledger.orders.length === 0 ? (
                  <tr><td colSpan={9} style={styles.emptyCell}>No orders marked yet. Click "Mark a Sale" to start.</td></tr>
                ) : (
                  ledger.orders.map((o) => (
                    <tr key={o.mark_id}>
                      <td style={styles.td}><strong>{o.invoice_no}</strong></td>
                      <td style={styles.td}>{fmtDate(o.invoice_date)}</td>
                      <td style={styles.td}>{o.customer_name}</td>
                      <td style={styles.tdR}>{fmtMoney(o.revenue)}</td>
                      <td style={styles.tdR}>{fmtMoney(o.cogs)}</td>
                      <td style={styles.tdR}>{fmtMoney(o.expenses)}</td>
                      <td style={styles.tdR}><strong>{fmtMoney(o.net_profit)}</strong></td>
                      <td style={{ ...styles.tdR, color: '#8e44ad', fontWeight: 700 }}>{fmtMoney(o.share)}</td>
                      <td style={styles.tdC}>
                        <button style={styles.unmarkBtn} onClick={() => handleUnmark(o.mark_id, o.invoice_no)} title="Unmark">✕</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              {ledger.orders.length > 0 && (
                <tfoot>
                  <tr>
                    <td colSpan={6} style={styles.tfootLabel}>Total Share</td>
                    <td style={styles.tdR}></td>
                    <td style={{ ...styles.tdR, color: '#8e44ad', fontWeight: 700 }}>{fmtMoney(t.total_share)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* Manual entries */}
          <div style={styles.sectionLabel}>Expenses & Advances</div>
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Date</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.thR}>Amount</th>
                  <th style={styles.thC}></th>
                </tr>
              </thead>
              <tbody>
                {ledger.entries.length === 0 ? (
                  <tr><td colSpan={5} style={styles.emptyCell}>No expenses or advances recorded.</td></tr>
                ) : (
                  ledger.entries.map((e) => {
                    const isExp = e.entry_type === 'expense';
                    return (
                      <tr key={e.id}>
                        <td style={styles.td}>{fmtDate(e.entry_date)}</td>
                        <td style={styles.td}>
                          <span style={{ ...styles.badge, background: isExp ? '#eafaf1' : '#fdedec', color: isExp ? '#27ae60' : '#e74c3c' }}>
                            {isExp ? 'Expense (credit)' : 'Advance (debit)'}
                          </span>
                        </td>
                        <td style={styles.td}>{e.description || '—'}</td>
                        <td style={{ ...styles.tdR, color: isExp ? '#27ae60' : '#e74c3c', fontWeight: 700 }}>
                          {isExp ? '+' : '−'} {fmtMoney(e.amount)}
                        </td>
                        <td style={styles.tdC}>
                          <button style={styles.unmarkBtn} onClick={() => handleDeleteEntry(e.id)} title="Delete">✕</button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* Modals */}
      {showMark && agent && (
        <MarkSaleModal agent={agent} onClose={() => setShowMark(false)} onSaved={refresh} />
      )}
      {entryMode && agent && (
        <AgentEntryModal agent={agent} mode={entryMode} onClose={() => setEntryMode(null)} onSaved={refresh} />
      )}
    </div>
  );
};

const styles = {
  wrap: { padding: '4px 0' },
  topBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: '16px', flexWrap: 'wrap', marginBottom: '20px' },
  agentPick: { display: 'flex', flexDirection: 'column', gap: '6px' },
  pickLabel: { fontSize: '12px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px' },
  select: { padding: '10px 14px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', fontFamily: 'inherit', minWidth: '200px' },
  actions: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  btnPrimary: { padding: '10px 18px', background: '#8e44ad', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  btnGreen: { padding: '10px 18px', background: '#27ae60', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  btnRed: { padding: '10px 18px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },

  totalsRow: { display: 'flex', gap: '14px', flexWrap: 'wrap', marginBottom: '24px' },
  totalCard: { flex: 1, minWidth: '160px', background: '#f8f9fa', borderRadius: '10px', padding: '16px 18px', border: '1px solid #e0e0e0' },
  balanceCard: { background: 'linear-gradient(135deg, #8e44ad, #6c3483)', border: 'none' },
  totalLabel: { fontSize: '12px', fontWeight: '600', color: '#7f8c8d', marginBottom: '8px' },
  totalVal: { fontSize: '20px', fontWeight: '700' },

  sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '8px', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0' },
  tableWrap: { overflowX: 'auto', marginBottom: '24px', border: '1px solid #e0e0e0', borderRadius: '10px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', padding: '12px 14px', background: '#f8f9fa', color: '#7f8c8d', fontWeight: '700', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' },
  thR: { textAlign: 'right', padding: '12px 14px', background: '#f8f9fa', color: '#7f8c8d', fontWeight: '700', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.3px', borderBottom: '1px solid #e0e0e0', whiteSpace: 'nowrap' },
  thC: { width: '40px', background: '#f8f9fa', borderBottom: '1px solid #e0e0e0' },
  td: { padding: '11px 14px', borderBottom: '1px solid #f0f0f0', color: '#2c3e50' },
  tdR: { padding: '11px 14px', borderBottom: '1px solid #f0f0f0', color: '#2c3e50', textAlign: 'right', whiteSpace: 'nowrap' },
  tdC: { padding: '11px 8px', borderBottom: '1px solid #f0f0f0', textAlign: 'center' },
  tfootLabel: { padding: '12px 14px', textAlign: 'right', fontWeight: '700', color: '#2c3e50', background: '#f8f9fa' },
  emptyCell: { padding: '24px', textAlign: 'center', color: '#95a5a6' },
  empty: { padding: '30px', textAlign: 'center', color: '#95a5a6', fontSize: '14px' },
  badge: { padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' },
  unmarkBtn: { width: '26px', height: '26px', borderRadius: '6px', border: 'none', background: '#f0f0f0', color: '#e74c3c', cursor: 'pointer', fontWeight: '700', fontSize: '13px' },
};

export default ProfitShareSection;